/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable lodash/import-scope */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/order */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prettier/prettier */
import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  AbuseDetailsKeys,
  AbuseDetailsLevel1VariantKeys,
  AbuseDetailsLevel2VariantKeys,
  AbuseDetailsLevel3VariantKeys,
  AbuseDetailsLevel4VariantKeys,
  AbuseDetailsLevel5VariantKeys,
  Cases,
  ClaimNumber,
  EIncludeExcludeKeys,
  EIncludeExcludeValue,
  StaticPropertyKey,
  VictimInfoGenderVariantKeys,
  VictimInfoKeys,
} from '@bvi/api-interfaces/entity/case-property';
import { AdditionalFilterParameter } from '@bvi/api-interfaces/request/case';
import {
  Autocomplete,
  IOnOptionsLoadParameters,
  Div,
  FormDateRangePicker,
  Select,
  Select2,
  HelpTooltip,
  FormDatePicker,
} from '@bvi/common-components';
import { useFormOptions } from '@bvi/form-utils';
import { DashboardRoutesPaths } from '@bvi/dashboard/shared/constants/routes';

import { ISearchFormDataSchema, OnOptionsLoad, useSearchContext } from '../../../lib';
import { i18nInstance } from '../../../locales';
import { styles } from '../styles';
import dayjs from 'dayjs';
import { omit } from 'lodash';

type InitialSearchProperties = {
  onOptionsLoad: OnOptionsLoad;
  isFormLoading?: boolean;
};

export const InitialSearch: React.FC<InitialSearchProperties> = (
  properties,
) => {
  const location = useLocation();
  const [searchParameters] = useSearchParams();
  const [claimNumberValue, setClaimNumberValue] = useState<string>(
    searchParameters.get(ClaimNumber.NAME) ?? '',
  );
  const { onOptionsLoad, isFormLoading } = properties;
  const localStorageKey = EIncludeExcludeKeys.CLASS_ACTION_NAME;
  const { control, setValue } = useFormContext<ISearchFormDataSchema>();
  const { defaultExcludeClassAction = true } = useSearchContext();
  const defaultClassAction = defaultExcludeClassAction
    ? EIncludeExcludeValue.YES
    : EIncludeExcludeValue.NO;
  const [localValue, setLocalValue] = useState<EIncludeExcludeValue>(() => {
    const storedValue = localStorage.getItem(localStorageKey);
    return (storedValue ?? defaultClassAction) as EIncludeExcludeValue;
  });

  useEffect(() => {
    const parameters = new URLSearchParams(location.search);
    const classActionParameter = parameters.get(localStorageKey);

    let nextValue: EIncludeExcludeValue;
    if (classActionParameter === EIncludeExcludeValue.YES) {
      nextValue = EIncludeExcludeValue.YES;
    } else if (classActionParameter === EIncludeExcludeValue.NO) {
      nextValue = EIncludeExcludeValue.NO;
    } else {
      // URL omits classAction — use app default (exclude on client, include on admin)
      nextValue = defaultClassAction;
    }

    setLocalValue(nextValue);
    setValue(localStorageKey, nextValue);
    localStorage.setItem(localStorageKey, nextValue);
  }, [location.search, defaultClassAction, localStorageKey, setValue]);

  useEffect(() => {
    setValue(ClaimNumber.NAME, claimNumberValue);
  }, [claimNumberValue, setValue]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as EIncludeExcludeValue;
    localStorage.setItem(localStorageKey, value);
    setLocalValue(value);
    setValue(localStorageKey, value);
  };

  const { t } = useTranslation('', {
    i18n: i18nInstance,
    keyPrefix: 'form',
  });

  const formattedGenderOptions =
    location.pathname == DashboardRoutesPaths.SEARCH
      ? omit(VictimInfoGenderVariantKeys, 'UNKNOWN')
      : VictimInfoGenderVariantKeys;

  const genderOptions = useFormOptions(formattedGenderOptions, 'gender');

  const level1Variants = useFormOptions(
    AbuseDetailsLevel1VariantKeys,
    'abuseTypeItems',
  );
  const level2Variants = useFormOptions(
    AbuseDetailsLevel2VariantKeys,
    'abuseTypeItems',
  );
  const level3Variants = useFormOptions(
    AbuseDetailsLevel3VariantKeys,
    'abuseTypeItems',
  );
  const level4Variants = useFormOptions(
    AbuseDetailsLevel4VariantKeys,
    'abuseTypeItems',
  );
  const level5Variants = useFormOptions(
    AbuseDetailsLevel5VariantKeys,
    'abuseTypeItems',
  );

  const maxSeverityItems = useMemo(
    () => [
      {
        id: '1',
        name: t(`${AdditionalFilterParameter.MAX_SEVERITY_LEVEL}.level1`),
      },
      {
        id: '2',
        name: t(`${AdditionalFilterParameter.MAX_SEVERITY_LEVEL}.level2`),
      },
      {
        id: '3',
        name: t(`${AdditionalFilterParameter.MAX_SEVERITY_LEVEL}.level3`),
      },
      {
        id: '4',
        name: t(`${AdditionalFilterParameter.MAX_SEVERITY_LEVEL}.level4`),
      },
      {
        id: '5',
        name: t(`${AdditionalFilterParameter.MAX_SEVERITY_LEVEL}.level5`),
      },
    ],
    [t],
  );

  const resolutionDateLow: any = useWatch({
    control,
    name: AdditionalFilterParameter.RESOLUTION_DATE_LOW,
  });
  const resolutionDateHigh: any = useWatch({
    control,
    name: AdditionalFilterParameter.RESOLUTION_DATE_HIGH,
  });

  useEffect(() => {
    if (resolutionDateLow) {
      setValue(
        AdditionalFilterParameter.RESOLUTION_DATE_LOW,
        dateFormatter(resolutionDateLow as Date),
      );
    }
    if (resolutionDateHigh) {
      setValue(
        AdditionalFilterParameter.RESOLUTION_DATE_HIGH,
        dateFormatter(resolutionDateHigh as Date),
      );
    }
  }, [resolutionDateLow, resolutionDateHigh]);

  const dateFormatter = (date: Date | null) => {
    if (!date) {
      return null;
    }

    return dayjs(date)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toDate()
      .getTime();
  };

  const handleLoadJobOptions = async (data: IOnOptionsLoadParameters) =>
    await onOptionsLoad(StaticPropertyKey.JOB_TITLE, data);

  const handleLoadPerpetratorOptions = async (data: IOnOptionsLoadParameters) =>
    await onOptionsLoad(StaticPropertyKey.PERPETRATOR, data);

  return (
    <Div>
      <Typography variant="h4" sx={styles.subtitle}>
        {t('includeExcludeInformation.title')}
      </Typography>

      <Grid item xs={12} my={4}>
        <FormControl fullWidth>
          <InputLabel>
            {t(`${EIncludeExcludeKeys.CLASS_ACTION_NAME}.label`)}
          </InputLabel>
          <Controller
            name={EIncludeExcludeKeys.CLASS_ACTION_NAME}
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                row
                style={{ marginTop: 8 }}
                value={localValue}
                onChange={(event) => handleRadioChange(event)}
              >
                <FormControlLabel
                  value={EIncludeExcludeValue.NO}
                  control={<Radio />}
                  label="Include"
                />
                <FormControlLabel
                  value={EIncludeExcludeValue.YES}
                  control={<Radio />}
                  label="Exclude"
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} my={4}>
        <FormControl fullWidth>
          <InputLabel>
            {t(`${EIncludeExcludeKeys.BANKRUPTCY_NAME}.label`)}
          </InputLabel>
          <Controller
            name={EIncludeExcludeKeys.BANKRUPTCY_NAME}
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                row
                style={{ marginTop: 8 }}
                value={field.value ?? EIncludeExcludeValue.NO}
              >
                <FormControlLabel
                  value={EIncludeExcludeValue.NO}
                  control={<Radio />}
                  label="Include"
                />
                <FormControlLabel
                  value={EIncludeExcludeValue.YES}
                  control={<Radio />}
                  label="Exclude"
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} my={4}>
        <FormControl fullWidth>
          <InputLabel>
            {t(`${EIncludeExcludeKeys.COMPENSATION_REDRESS_NAME}.label`)}
          </InputLabel>
          <Controller
            name={EIncludeExcludeKeys.COMPENSATION_REDRESS_NAME}
            control={control}
            render={({ field }) => (
              <RadioGroup
                {...field}
                row
                style={{ marginTop: 8 }}
                value={field.value ?? EIncludeExcludeValue.NO}
              >
                <FormControlLabel
                  value={EIncludeExcludeValue.NO}
                  control={<Radio />}
                  label="Include"
                />
                <FormControlLabel
                  value={EIncludeExcludeValue.YES}
                  control={<Radio />}
                  label="Exclude"
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      </Grid>

      {location.pathname.includes(Cases.title) && (
        <>
          <Typography variant="h4" sx={styles.subtitle}>
            {t('claimNumber.label')}
          </Typography>
          <Grid container columnSpacing={3}>
            <Grid item xs={12} my={2}>
              <TextField
                name={ClaimNumber.NAME}
                variant="filled"
                fullWidth
                label={t('claimNumber.label')}
                placeholder={t('claimNumber.placeholder')}
                value={claimNumberValue}
                onChange={(event) => {
                  setClaimNumberValue(event.target.value);
                  setValue(ClaimNumber.NAME, event.target.value);
                }}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Typography variant="h4" sx={styles.subtitle}>
        {t('victimInformation.title')}
      </Typography>
      <Grid container columnSpacing={3}>
        <Grid item xs={12} my={2}>
          <Select2
            name={VictimInfoKeys.GENDER}
            variant="filled"
            multiple
            items={genderOptions}
            fullWidth
            label={t(`${VictimInfoKeys.GENDER}.label`)}
            placeholder={t(`${VictimInfoKeys.GENDER}.placeholder`)}
          />
        </Grid>
      </Grid>

      <Typography variant="h4" sx={styles.subtitle}>
        {t('abuseDetails.title')}
        <span style={{ display: 'inline-block', marginTop: '-7px' }}>
          <HelpTooltip title="Select severity level of abuse or specific types of abuse for a more targeted search" />
        </span>
      </Typography>
      <Grid container columnSpacing={3}>
        <Grid container item xs={12} columnSpacing={3} my={2}>
          <Grid item xs={12} mb={3}>
            <Select
              field={AdditionalFilterParameter.MAX_SEVERITY_LEVEL}
              label={t(`${AdditionalFilterParameter.MAX_SEVERITY_LEVEL}.label`)}
              placeholder={t(
                `${AdditionalFilterParameter.MAX_SEVERITY_LEVEL}.placeholder`,
              )}
              items={maxSeverityItems}
              placeholderSelectable
              variant="filled"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} mb={3}>
            <Select2
              name={AbuseDetailsKeys.LEVEL_1}
              variant="filled"
              multiple
              items={level1Variants}
              fullWidth
              placeholder={t(`${AbuseDetailsKeys.LEVEL_1}.placeholder`)}
              label={t(`${AbuseDetailsKeys.LEVEL_1}.label`)}
            />
          </Grid>
          <Grid item xs={12} mb={3}>
            <Select2
              name={AbuseDetailsKeys.LEVEL_2}
              variant="filled"
              multiple
              items={level2Variants}
              fullWidth
              placeholder={t(`${AbuseDetailsKeys.LEVEL_2}.placeholder`)}
              label={t(`${AbuseDetailsKeys.LEVEL_2}.label`)}
            />
          </Grid>
          <Grid item xs={12} mb={3}>
            <Select2
              name={AbuseDetailsKeys.LEVEL_3}
              variant="filled"
              multiple
              items={level3Variants}
              fullWidth
              placeholder={t(`${AbuseDetailsKeys.LEVEL_3}.placeholder`)}
              label={t(`${AbuseDetailsKeys.LEVEL_3}.label`)}
            />
          </Grid>
          <Grid item xs={12} mb={3}>
            <Select2
              name={AbuseDetailsKeys.LEVEL_4}
              variant="filled"
              multiple
              items={level4Variants}
              fullWidth
              placeholder={t(`${AbuseDetailsKeys.LEVEL_4}.placeholder`)}
              label={t(`${AbuseDetailsKeys.LEVEL_4}.label`)}
            />
          </Grid>
          <Grid item xs={12}>
            <Select2
              name={AbuseDetailsKeys.LEVEL_5}
              variant="filled"
              multiple
              items={level5Variants}
              fullWidth
              placeholder={t(`${AbuseDetailsKeys.LEVEL_5}.placeholder`)}
              label={t(`${AbuseDetailsKeys.LEVEL_5}.label`)}
            />
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="h4" sx={styles.subtitle}>
        {t('responsibleParty.title')}
      </Typography>
      <Grid container columnSpacing={3}>
        <Grid item xs={12} my={2}>
          <Autocomplete
            name={StaticPropertyKey.PERPETRATOR}
            onOptionsLoad={handleLoadPerpetratorOptions}
            variant="filled"
            multiple
            isPrefetching={isFormLoading}
            label={t(`${StaticPropertyKey.PERPETRATOR}.label`)}
            placeholder={t(`${StaticPropertyKey.PERPETRATOR}.placeholder`)}
          />
        </Grid>
        <Grid item xs={12} my={2}>
          <Autocomplete
            name={StaticPropertyKey.JOB_TITLE}
            onOptionsLoad={handleLoadJobOptions}
            variant="filled"
            multiple
            isPrefetching={isFormLoading}
            label={t(`${StaticPropertyKey.JOB_TITLE}.label`)}
            placeholder={t(`${StaticPropertyKey.JOB_TITLE}.placeholder`)}
          />
        </Grid>
      </Grid>

      <Typography variant="h4" sx={styles.subtitle}>
        {t('resolutionDateRange.title')}
        <span style={{ display: 'inline-block', marginTop: '-7px' }}>
          <HelpTooltip title="Restrict search to reflect claim values before, after, or between specific dates" />
        </span>
      </Typography>
      <Grid container item xs={12} columnSpacing={3} mb={2}>
        <Grid item xs={12} sm={6}>
          <FormDatePicker
            field={AdditionalFilterParameter.RESOLUTION_DATE_LOW}
            fullWidth
            variant="filled"
            valueFormat="jsDate"
            placeholder={t(
              `${AdditionalFilterParameter.RESOLUTION_DATE_LOW}.placeholder`,
            )}
            // error={Boolean(errors?.resolution?.resolutionDateLow)}
            // helperText={<>{errors?.resolution?.resolutionDateLow?.message}</>}
            maxDate={
              resolutionDateLow
                ? dayjs(resolutionDateHigh) // if you're also watching the high field
                : dayjs(new Date())
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormDatePicker
            field={AdditionalFilterParameter.RESOLUTION_DATE_HIGH}
            fullWidth
            variant="filled"
            valueFormat="jsDate"
            placeholder={t(
              `${AdditionalFilterParameter.RESOLUTION_DATE_HIGH}.placeholder`,
            )}
            // error={Boolean(errors?.resolution?.resolutionDateHigh)}
            // helperText={<>{errors?.resolution?.resolutionDateHigh?.message}</>}
            // maxDate={dayjs(new Date())}
            minDate={resolutionDateLow ? dayjs(resolutionDateLow) : undefined}
          />
        </Grid>
        {/* <FormDateRangePicker
          fields={[
            AdditionalFilterParameter.RESOLUTION_DATE_LOW,
            AdditionalFilterParameter.RESOLUTION_DATE_HIGH,
          ]}
          placeholders={[
            t(`${AdditionalFilterParameter.RESOLUTION_DATE_LOW}.placeholder`),
            t(`${AdditionalFilterParameter.RESOLUTION_DATE_HIGH}.placeholder`),
          ]}
          valueFormat="jsUtcDate"
        /> */}
      </Grid>
    </Div>
  );
};
